/**
 * usePresenceSync — Pont RTDB → Firestore côté CLIENT
 *
 * OPTIMISATION : Ce hook n'écoute plus TOUS les shops de la base.
 * Il prend un shopId en paramètre et n'écoute QUE ce nœud RTDB.
 *
 * Avant : getDocs(collection('shops')) au montage = N lectures Firestore
 *         pour CHAQUE utilisateur connecté → quota explosé.
 *
 * Maintenant : 0 lecture Firestore au montage, 1 seul listener RTDB
 *              actif uniquement pour le marchand connecté, avec son shopId.
 */

import { useEffect, useRef } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, onValue, off } from 'firebase/database';
import { db, rtdb } from '../firebase/config';

export const usePresenceSync = (shopId) => {
  /* writingLock LOCAL à l'instance — plus de variable module-level partagée */
  const writingLockRef = useRef(false);

  useEffect(() => {
    if (!rtdb || !shopId) return;

    const presenceRef = ref(rtdb, `presence/${shopId}`);

    const handlePresence = async (snapshot) => {
      const presence = snapshot.val();

      /* On n'agit que quand RTDB signale "uncertain" (onDisconnect déclenché) */
      if (!presence || presence.status !== 'uncertain') return;

      /* Verrou local pour éviter les doubles écritures */
      if (writingLockRef.current) return;
      writingLockRef.current = true;

      try {
        const shopSnap = await getDoc(doc(db, 'shops', shopId));
        if (!shopSnap.exists()) return;

        const shopData = shopSnap.data();

        /* Ne jamais passer en uncertain un shop géré par les horaires */
        if (shopData?.managedBy === 'schedule') return;

        /* Ne rien faire si le shop est déjà fermé/uncertain */
        if (shopData?.status !== 'open') return;

        await updateDoc(doc(db, 'shops', shopId), {
          status:    'uncertain',
          updatedAt: serverTimestamp(),
          updatedBy: 'onDisconnect',
        });
        console.log(`usePresenceSync: shop ${shopId} → uncertain`);
      } catch (err) {
        console.error(`usePresenceSync: erreur shop ${shopId}`, err);
      } finally {
        /* Libérer le verrou après 10s */
        setTimeout(() => { writingLockRef.current = false; }, 10_000);
      }
    };

    onValue(presenceRef, handlePresence);

    return () => {
      off(presenceRef, 'value', handlePresence);
    };
  }, [shopId]);
};